// AI Recording Studio Pro — Export Manager
// Multi-format export with progress tracking
import { useState } from "react";
import { RecordingStudioLayout } from "@/components/layout/RecordingStudioLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import {
  Download, Film, Music, Subtitles, FileText, Image,
  Settings2, CheckCircle2, Clock, HardDrive, Loader2,
  Play, Zap, MonitorPlay, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const VIDEO_FORMATS = [
  { id: "webm", label: "WebM", ext: ".webm", desc: "Format web natif, léger", icon: "🌐" },
  { id: "mp4", label: "MP4 (H.264)", ext: ".mp4", desc: "Compatible partout", icon: "🎬" },
  { id: "mov", label: "MOV", ext: ".mov", desc: "Apple / Final Cut Pro", icon: "🍎" },
  { id: "mkv", label: "MKV", ext: ".mkv", desc: "Haute qualité, multi-pistes", icon: "📦" },
  { id: "gif", label: "GIF animé", ext: ".gif", desc: "Court, sans audio", icon: "🎞️" },
];

const AUDIO_FORMATS = [
  { id: "mp3", label: "MP3", ext: ".mp3", desc: "Audio universel", icon: "🎵" },
  { id: "wav", label: "WAV", ext: ".wav", desc: "Audio non compressé", icon: "🎶" },
  { id: "ogg", label: "OGG", ext: ".ogg", desc: "Audio open-source", icon: "🔊" },
];

const SUBTITLE_FORMATS = [
  { id: "srt", label: "SRT", ext: ".srt", desc: "Standard sous-titres", icon: "📝" },
  { id: "vtt", label: "WebVTT", ext: ".vtt", desc: "Web sous-titres", icon: "🌐" },
];

const DOCUMENT_FORMATS = [
  { id: "pdf", label: "PDF", ext: ".pdf", desc: "Document formaté", icon: "📄" },
  { id: "md", label: "Markdown", ext: ".md", desc: "Texte structuré", icon: "📋" },
  { id: "html", label: "HTML", ext: ".html", desc: "Page web", icon: "🌍" },
  { id: "txt", label: "Texte brut", ext: ".txt", desc: "Texte simple", icon: "📃" },
];

const QUALITY_PRESETS = [
  { id: "low", label: "Basse", desc: "720p · 2 Mbps", bitrate: 2000, resolution: "720p" },
  { id: "medium", label: "Moyenne", desc: "1080p · 5 Mbps", bitrate: 5000, resolution: "1080p" },
  { id: "high", label: "Haute", desc: "1080p · 10 Mbps", bitrate: 10000, resolution: "1080p" },
  { id: "ultra", label: "Ultra", desc: "2K · 15 Mbps", bitrate: 15000, resolution: "2k" },
  { id: "max", label: "Maximum", desc: "4K · 25 Mbps", bitrate: 25000, resolution: "4k" },
];

export default function RecordingExport() {
  const [activeTab, setActiveTab] = useState("video");
  const [selectedFormat, setSelectedFormat] = useState("webm");
  const [quality, setQuality] = useState("medium");
  const [includeSubtitles, setIncludeSubtitles] = useState(false);
  const [includeWatermark, setIncludeWatermark] = useState(false);
  const [watermarkText, setWatermarkText] = useState("LearnDevOps");
  const [filename, setFilename] = useState("recording");
  const [exportHistory, setExportHistory] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Simulate export
  const handleExport = () => {
    if (!filename.trim()) return toast.error("Nom de fichier requis");

    setExporting(true);
    setExportProgress(0);

    const allFormats = [...VIDEO_FORMATS, ...AUDIO_FORMATS, ...SUBTITLE_FORMATS, ...DOCUMENT_FORMATS];
    const fmt = allFormats.find((f) => f.id === selectedFormat);
    const qualityPreset = QUALITY_PRESETS.find((q) => q.id === quality);

    // Simulate progress
    const interval = setInterval(() => {
      setExportProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setExporting(false);
          const entry = {
            id: Date.now(),
            filename: `${filename}${fmt?.ext || ".webm"}`,
            format: fmt?.label || selectedFormat,
            quality: qualityPreset?.label || quality,
            timestamp: new Date().toISOString(),
            status: "completed",
          };
          setExportHistory((prev) => [entry, ...prev]);
          toast.success(`Export terminé : ${entry.filename}`);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 300);
  };

  const allFormats = { video: VIDEO_FORMATS, audio: AUDIO_FORMATS, subtitle: SUBTITLE_FORMATS, document: DOCUMENT_FORMATS };
  const currentFormats = allFormats[activeTab] || [];

  return (
    <RecordingStudioLayout>
      <div className="p-6 md:p-8 space-y-6 max-w-[1000px] mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #8b5cf6, #ec4899)", boxShadow: "0 4px 12px rgba(139,92,246,0.3)" }}>
              <Download className="h-5 w-5 text-white" />
            </div>
            Export
          </h1>
          <p className="text-sm text-muted-foreground mt-1 ml-[52px]">
            Exportez vos enregistrements dans de multiples formats
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Format selection */}
          <div className="lg:col-span-2 space-y-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="video" className="text-xs gap-1"><Film className="h-3 w-3" /> Vidéo</TabsTrigger>
                <TabsTrigger value="audio" className="text-xs gap-1"><Music className="h-3 w-3" /> Audio</TabsTrigger>
                <TabsTrigger value="subtitle" className="text-xs gap-1"><Subtitles className="h-3 w-3" /> Sous-titres</TabsTrigger>
                <TabsTrigger value="document" className="text-xs gap-1"><FileText className="h-3 w-3" /> Documents</TabsTrigger>
              </TabsList>

              {Object.entries(allFormats).map(([key, formats]) => (
                <TabsContent key={key} value={key} className="mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {formats.map((fmt) => (
                      <motion.button key={fmt.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        onClick={() => setSelectedFormat(fmt.id)}
                        className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${
                          selectedFormat === fmt.id ? "border-primary bg-primary/5 shadow-sm" : "border-border/50 hover:border-primary/30"
                        }`}>
                        <span className="text-2xl">{fmt.icon}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold">{fmt.label}</p>
                          <p className="text-[11px] text-muted-foreground">{fmt.desc}</p>
                        </div>
                        {selectedFormat === fmt.id && (
                          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 ml-auto" />
                        )}
                      </motion.button>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>

            {/* Quality presets (video only) */}
            {activeTab === "video" && (
              <Card>
                <CardHeader className="pb-3 pt-4 px-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" /> Qualité
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-4 px-4 pt-0">
                  <div className="grid grid-cols-5 gap-2">
                    {QUALITY_PRESETS.map((q) => (
                      <button key={q.id} onClick={() => setQuality(q.id)}
                        className={`p-2.5 rounded-lg border text-center transition-all ${
                          quality === q.id ? "border-primary bg-primary/5" : "border-border/50 hover:border-primary/30"
                        }`}>
                        <p className="text-xs font-semibold">{q.label}</p>
                        <p className="text-[9px] text-muted-foreground mt-0.5">{q.desc}</p>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Export progress */}
            {exporting && (
              <Card className="border-primary/30">
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Loader2 className="h-5 w-5 text-primary animate-spin" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">Export en cours...</p>
                      <p className="text-xs text-muted-foreground">{filename}{VIDEO_FORMATS.find((f) => f.id === selectedFormat)?.ext || ""}</p>
                    </div>
                    <span className="text-sm font-mono font-bold text-primary">{Math.min(100, Math.round(exportProgress))}%</span>
                  </div>
                  <Progress value={Math.min(100, exportProgress)} className="h-2" />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Options + Actions */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-primary" /> Options
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 px-4 pt-0 space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Nom du fichier</label>
                  <Input value={filename} onChange={(e) => setFilename(e.target.value)}
                    placeholder="recording" className="h-8 text-xs" />
                </div>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs">Inclure sous-titres</span>
                    <Switch checked={includeSubtitles} onCheckedChange={setIncludeSubtitles} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs">Watermark</span>
                    <Switch checked={includeWatermark} onCheckedChange={setIncludeWatermark} />
                  </div>
                  {includeWatermark && (
                    <Input value={watermarkText} onChange={(e) => setWatermarkText(e.target.value)}
                      placeholder="LearnDevOps" className="h-8 text-xs" />
                  )}
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleExport} disabled={exporting} className="w-full gap-2"
              style={{ background: "linear-gradient(135deg, #8b5cf6, #ec4899)", border: "none" }}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exporting ? "Export en cours..." : "Exporter"}
            </Button>

            {/* History */}
            {exportHistory.length > 0 && (
              <Card>
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" /> Historique
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-3 px-4 pt-0">
                  <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                    {exportHistory.map((item) => (
                      <div key={item.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/50">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium truncate">{item.filename}</p>
                          <p className="text-[10px] text-muted-foreground">{item.format} · {item.quality}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </RecordingStudioLayout>
  );
}
